import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, Car, Home as HomeIcon, Building2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { LogoMark, WordMark } from "@/components/Logo";
import { submitQuote } from "@/lib/api";
import usePageMeta from "@/hooks/usePageMeta";

const TYPES = [
  { key: "auto", label: "Auto", icon: Car, desc: "Motor, personal vehicles, classic cars." },
  { key: "home", label: "Home", icon: HomeIcon, desc: "Homeowners, tenants, condo, cottage." },
  { key: "commercial", label: "Commercial", icon: Building2, desc: "Business, liability, property, workers." },
];

const initial = {
  insurance_type: "",
  full_name: "",
  email: "",
  phone: "",
  license_number: "",
  vin: "",
  coverage_type: "",
  property_type: "",
  business_name: "",
  business_type: "",
  quote_stage: "",
  stage_date: "",
  coverage_notes: "",
};

const slide = {
  initial: { opacity: 0, x: 40 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -40 },
  transition: { duration: 0.45, ease: [0.19, 1, 0.22, 1] },
};

export default function Quote() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [step, setStep] = useState(1);
  const [data, setData] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  usePageMeta({
    title: "Get a Free Insurance Quote — Auto, Home & Commercial | AlwaysInsured",
    description: "Free quote for auto, home, or commercial insurance in Ontario — takes under a minute. Licensed broker with 40+ carrier partners. We reply within one business day.",
  });

  useEffect(() => {
    const t = params.get("type");
    if (t && ["auto", "home", "commercial"].includes(t)) {
      setData((d) => ({ ...d, insurance_type: t }));
      setStep(2);
    }
  }, [params]);

  const totalSteps = 3;
  const set = (k) => (e) => setData({ ...data, [k]: e.target.value });

  const pickType = (t) => {
    setData({ ...data, insurance_type: t });
    setTimeout(() => setStep(2), 150);
  };

  const canNext = () => {
    if (step === 1) return !!data.insurance_type;
    if (step === 2) return data.full_name.trim() && /\S+@\S+\.\S+/.test(data.email) && data.phone.trim();
    return true;
  };

  const next = () => {
    if (!canNext()) {
      toast.error("Please fill in the required fields.");
      return;
    }
    setStep((s) => Math.min(s + 1, totalSteps));
  };
  const back = () => setStep((s) => Math.max(s - 1, 1));

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await submitQuote(data);
      setDone(true);
      toast.success("Quote request received.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div data-testid="quote-success" className="min-h-screen bg-forest-900 flex items-center justify-center px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-lg text-center"
        >
          <div className="w-14 h-14 mx-auto mb-8 border border-bone-100/30 flex items-center justify-center">
            <Check size={22} className="text-bone-100" />
          </div>
          <h1 className="font-serif text-5xl md:text-6xl text-bone-100 leading-none tracking-tight">
            Thank you.
          </h1>
          <div className="overline text-terracotta-500 mt-4">Received</div>
          <p className="mt-6 text-bone-100/70 leading-relaxed">
            We've received your request and will be in touch within one business day at{" "}
            <strong className="text-bone-100">{data.email}</strong>. If it's urgent, email us directly at{" "}
            <a href="mailto:Alwaysinsured@hotmail.com" className="underline">Alwaysinsured@hotmail.com</a>.
          </p>
          <Link
            to="/"
            data-testid="quote-back-home"
            className="mt-10 inline-flex items-center gap-2 border-b border-bone-100/40 pb-1 text-bone-100 hover:border-terracotta-500 hover:text-terracotta-400"
          >
            <ArrowLeft size={16} /> Back to home
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div data-testid="quote-page" className="min-h-screen bg-forest-900 px-6 md:px-12 lg:px-16 py-12">
      <div className="max-w-[1200px] mx-auto">
        {/* Top bar */}
        <div className="flex items-center justify-between mb-16">
          <Link
            to="/"
            data-testid="quote-logo-home"
            className="inline-flex items-center gap-3 text-bone-100"
          >
            <LogoMark size={32} />
            <WordMark className="text-2xl md:text-[1.75rem]" />
          </Link>
          <div className="overline text-bone-100/50">Step {step} of {totalSteps}</div>
        </div>

        {/* Progress line */}
        <div className="h-px bg-bone-100/10 relative mb-14">
          <motion.div
            className="absolute top-0 left-0 h-px bg-terracotta-500"
            initial={false}
            animate={{ width: `${(step / totalSteps) * 100}%` }}
            transition={{ duration: 0.5, ease: [0.19, 1, 0.22, 1] }}
          />
        </div>

        <div className="min-h-[440px]">
          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.div key="step1" {...slide}>
                <h1 className="font-serif text-4xl md:text-6xl text-bone-100 leading-[1.05] tracking-tight max-w-2xl">
                  What are we <span className="italic">insuring?</span>
                </h1>
                <div className="overline text-terracotta-500 mt-4">Step one</div>
                <p className="mt-4 text-bone-100/70 max-w-md">Pick the coverage you need. You can adjust everything on the next step.</p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
                  {TYPES.map((t) => {
                    const Icon = t.icon;
                    const active = data.insurance_type === t.key;
                    return (
                      <button
                        className={`text-left p-8 border transition-all hover:-translate-y-1 duration-500 ${
                          active
                            ? "border-terracotta-500 bg-forest-800 text-bone-100"
                            : "border-bone-100/20 bg-forest-800/40 hover:border-bone-100/60"
                        }`}
                        onClick={() => pickType(t.key)}
                        data-testid={`quote-type-${t.key}`}
                        key={t.key}
                      >
                        <Icon size={28} className={active ? "text-terracotta-400" : "text-bone-100"} />
                        <div className="font-serif text-3xl mt-8 leading-none">{t.label}</div>
                        <div className={`overline mt-2 ${active ? "text-bone-100/60" : "text-bone-100/60"}`}>
                          Coverage
                        </div>
                        <div className={`text-sm mt-4 leading-relaxed ${active ? "text-bone-100/80" : "text-bone-100/70"}`}>
                          {t.desc}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div key="step2" {...slide}>
                <h1 className="font-serif text-4xl md:text-6xl text-bone-100 leading-[1.05] tracking-tight max-w-2xl">
                  About <span className="italic">you.</span>
                </h1>
                <div className="overline text-terracotta-500 mt-4">Step two</div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-12 max-w-3xl">
                  <Field label="Full name *">
                    <Input data-testid="quote-name-input" value={data.full_name} onChange={set("full_name")} placeholder="Jane Doe" />
                  </Field>
                  <Field label="Email *">
                    <Input data-testid="quote-email-input" type="email" value={data.email} onChange={set("email")} placeholder="jane@example.com" />
                  </Field>
                  <Field label="Phone *">
                    <Input data-testid="quote-phone-input" value={data.phone} onChange={set("phone")} placeholder="(416) 555 0123" />
                  </Field>
                </div>
              </motion.div>
            )}

            {step === 3 && (
              <motion.div key="step3" {...slide}>
                <h1 className="font-serif text-4xl md:text-6xl text-bone-100 leading-[1.05] tracking-tight max-w-2xl">
                  Almost <span className="italic">there.</span>
                </h1>
                <div className="overline text-terracotta-500 mt-4">Step three</div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-12 max-w-3xl">
                  {data.insurance_type === "auto" && (
                    <>
                      <Field label="Driver's license number">
                        <Input data-testid="quote-license-number" value={data.license_number} onChange={set("license_number")} placeholder="D1234-56789-01234" />
                      </Field>
                      <Field label="Vehicle VIN">
                        <Input data-testid="quote-vin" value={data.vin} onChange={set("vin")} placeholder="1HGCM82633A123456" />
                      </Field>
                      <div className="md:col-span-2">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          {[
                            { key: "full", label: "Full coverage", desc: "Collision, comprehensive, liability. Broadest protection." },
                            { key: "third_party", label: "Third-party coverage", desc: "Liability for damage or injury to others only. Lower premium." },
                          ].map((opt) => {
                            const active = data.coverage_type === opt.key;
                            return (
                              <button
                                key={opt.key}
                                type="button"
                                data-testid={`coverage-${opt.key}`}
                                onClick={() => setData({ ...data, coverage_type: opt.key })}
                                className={`text-left p-5 border transition-all ${
                                  active
                                    ? "border-terracotta-500 bg-forest-800 text-bone-100"
                                    : "border-bone-100/20 bg-forest-800/40 hover:border-bone-100/60"
                                }`}
                              >
                                <div className="font-serif text-xl">{opt.label}</div>
                                <div className={`overline mt-1 ${active ? "text-terracotta-400" : "text-bone-100/60"}`}>Option</div>
                                <div className={`text-sm mt-2 leading-relaxed ${active ? "text-bone-100/80" : "text-bone-100/70"}`}>
                                  {opt.desc}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                        <Label className="overline text-bone-100/70 mt-4 block">Coverage type</Label>
                      </div>
                    </>
                  )}
                  {data.insurance_type === "home" && (
                    <>
                      <Field label="Property type">
                        <Input data-testid="quote-property-type" value={data.property_type} onChange={set("property_type")} placeholder="Detached house / Condo / Tenant" />
                      </Field>
                    </>
                  )}
                  {data.insurance_type === "commercial" && (
                    <>
                      <Field label="Business name">
                        <Input data-testid="quote-business-name" value={data.business_name} onChange={set("business_name")} placeholder="Acme Ltd." />
                      </Field>
                      <Field label="Business type / industry">
                        <Input data-testid="quote-business-type" value={data.business_type} onChange={set("business_type")} placeholder="Retail, trades, professional services..." />
                      </Field>
                    </>
                  )}

                  {/* Urgency (applies to all insurance types) */}
                  <div className="md:col-span-2">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {[
                        { key: "new", label: "New purchase", desc: data.insurance_type === "auto" ? "Buying a new or replacement vehicle." : data.insurance_type === "home" ? "Just bought or moving into a new place." : "Starting or acquiring a new business asset." },
                        { key: "renewal", label: "Renewal", desc: "Existing policy — comparing carriers or switching." },
                      ].map((opt) => {
                        const active = data.quote_stage === opt.key;
                        return (
                          <button
                            key={opt.key}
                            type="button"
                            data-testid={`quote-stage-${opt.key}`}
                            onClick={() => setData({ ...data, quote_stage: opt.key, stage_date: "" })}
                            className={`text-left p-5 border transition-all ${
                              active
                                ? "border-terracotta-500 bg-forest-800 text-bone-100"
                                : "border-bone-100/20 bg-forest-800/40 hover:border-bone-100/60"
                            }`}
                          >
                            <div className="font-serif text-xl">{opt.label}</div>
                            <div className={`overline mt-1 ${active ? "text-terracotta-400" : "text-bone-100/60"}`}>Option</div>
                            <div className={`text-sm mt-2 leading-relaxed ${active ? "text-bone-100/80" : "text-bone-100/70"}`}>
                              {opt.desc}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    <Label className="overline text-bone-100/70 mt-4 block">Is this a new purchase or a renewal?</Label>
                  </div>

                  {data.quote_stage && (
                    <div className="md:col-span-2">
                      <Field
                        label={
                          data.quote_stage === "renewal"
                            ? "Renewal date"
                            : data.insurance_type === "auto"
                            ? "Expected purchase / delivery date"
                            : data.insurance_type === "home"
                            ? "Expected closing / move-in date"
                            : "Expected start / purchase date"
                        }
                      >
                        <Input
                          data-testid="quote-stage-date"
                          type="date"
                          value={data.stage_date}
                          onChange={set("stage_date")}
                        />
                      </Field>
                    </div>
                  )}

                  <div className="md:col-span-2">
                    <Field label="Anything else we should know?">
                      <Textarea
                        data-testid="quote-notes"
                        value={data.coverage_notes}
                        onChange={set("coverage_notes")}
                        rows={4}
                        placeholder="Current coverage, claims history, preferred carrier..."
                      />
                    </Field>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-16 pt-6 border-t border-bone-100/10">
          <button
            data-testid="quote-back-btn"
            onClick={step === 1 ? () => nav("/") : back}
            className="inline-flex items-center gap-2 text-bone-100/70 hover:text-bone-100 transition-colors"
          >
            <ArrowLeft size={16} /> {step === 1 ? "Home" : "Back"}
          </button>

          {step < totalSteps ? (
            <button
              data-testid="quote-next-btn"
              onClick={next}
              disabled={!canNext()}
              className="inline-flex items-center gap-3 bg-terracotta-500 text-forest-900 hover:bg-terracotta-400 disabled:opacity-40 disabled:cursor-not-allowed transition-colors px-8 py-4 font-medium"
            >
              Continue <ArrowRight size={16} />
            </button>
          ) : (
            <button
              data-testid="quote-submit-btn"
              onClick={submit}
              disabled={submitting}
              className="inline-flex items-center gap-3 bg-terracotta-500 text-forest-900 hover:bg-terracotta-400 disabled:opacity-40 transition-colors px-8 py-4 font-medium"
            >
              {submitting ? "Sending..." : "Submit request"} <ArrowRight size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <div className="border-b border-bone-100/25 focus-within:border-terracotta-500 transition-colors">
      {children}
    </div>
    <Label className="overline text-bone-100/70 mt-2 block">{label}</Label>
  </div>
);
